import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, CalendarDays, Droplet, Plus, ArrowRight, Clock } from "lucide-react";
import { api, fmtDate, fullName } from "@/lib/api";
import { StatCard, EmptyState } from "@/components/Field";
import { AppointmentDialog } from "@/components/AppointmentDialog";
import { AppointmentStatus } from "@/components/AppointmentStatus";

const Row = ({ a }) => (
  <div className="flex items-center gap-4 border-b border-[#e2e8f0] py-3 last:border-0" data-testid={`dashboard-appt-${a.id}`}>
    <div className="w-16 shrink-0 rounded-xl bg-[#edf2ef] py-2 text-center">
      <div className="text-[10px] font-semibold uppercase text-[#2f4f47]">{fmtDate(a.date, { month: "short" })}</div>
      <div className="text-lg font-extrabold leading-none">{fmtDate(a.date, { day: "2-digit" })}</div>
    </div>
    <div className="min-w-0 flex-1">
      <Link to={`/pazienti/${a.patient_id}`} className="font-semibold hover:text-[#3d655b]">{a.patient_name}</Link>
      <div className="truncate text-xs text-slate-500">{a.reason}{a.location ? ` · ${a.location}` : ""}</div>
      <div className="mt-1"><AppointmentStatus status={a.status} testid={`dashboard-appt-status-${a.id}`} /></div>
    </div>
    <div className="flex items-center gap-1 text-sm font-semibold text-slate-600"><Clock className="h-4 w-4" />{a.time}</div>
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/dashboard").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="fade-up space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-xs text-[#3d655b]">{new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</div>
          <h1 className="page-title mt-1">Buongiorno, Dottoressa</h1>
        </div>
        <button className="btn-primary" onClick={() => setOpen(true)} data-testid="dashboard-new-appointment-btn"><Plus className="h-4 w-4" /> Fissa appuntamento</button>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Pazienti" value={d?.patients_count ?? "–"} icon={Users} testid="stat-patients" dark />
        <StatCard label="Appuntamenti oggi" value={d?.appointments_today ?? "–"} icon={CalendarDays} testid="stat-appointments-today" />
        <StatCard label="Prelievi del mese" value={d?.draws_month ?? "–"} icon={Droplet} testid="stat-draws-month" />
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="card lg:col-span-2">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-lg font-bold">Prossimi appuntamenti</h2>
            <Link to="/appuntamenti" className="flex items-center gap-1 text-sm font-semibold text-[#3d655b]" data-testid="dashboard-go-appointments">Calendario <ArrowRight className="h-4 w-4" /></Link>
          </div>
          {d?.upcoming?.length ? d.upcoming.map((a) => <Row key={a.id} a={a} />) : <EmptyState icon={CalendarDays} title="Nessun appuntamento in programma" testid="dashboard-no-appointments" />}
        </div>
        <div className="space-y-6">
          <div className="card">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-bold">Agenda personale</h2>
              <Link to="/agenda" className="text-sm font-semibold text-[#3d655b]" data-testid="dashboard-go-agenda">Apri</Link>
            </div>
            {d?.agenda?.length ? d.agenda.map((e) => (
              <div key={e.id} className="flex justify-between border-b border-[#e2e8f0] py-2 text-sm last:border-0">
                <span className="truncate font-medium">{e.title}</span>
                <span className="shrink-0 text-slate-500">{fmtDate(e.date, { day: "2-digit", month: "short" })} {e.time}</span>
              </div>
            )) : <p className="text-sm text-slate-400">Nessun impegno personale</p>}
          </div>
          <div className="card">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-bold">Ultimi pazienti</h2>
              <Link to="/pazienti" className="text-sm font-semibold text-[#3d655b]" data-testid="dashboard-go-patients">Tutti</Link>
            </div>
            {d?.recent_patients?.length ? d.recent_patients.map((p) => (
              <Link key={p.id} to={`/pazienti/${p.id}`} className="flex items-center gap-3 rounded-xl py-2 hover:bg-slate-50" data-testid={`dashboard-patient-${p.id}`}>
                <div className="grid h-9 w-9 place-items-center rounded-full bg-[#edf2ef] text-xs font-bold text-[#2f4f47]">{p.first_name[0]}{p.last_name[0]}</div>
                <span className="text-sm font-medium">{fullName(p)}</span>
              </Link>
            )) : <p className="text-sm text-slate-400">Nessun paziente ancora</p>}
          </div>
        </div>
      </div>
      <AppointmentDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
